import React, { useContext, useEffect, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  CarrierContactPersonOutDTO,
  CatalogCarrierDetailOutDTO,
  Pageable,
  PermissionTypesEnum,
} from '@api/ringil-api.v2.tsx';
import SvgId from '@icons/id.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { ApiContext } from '@api/api.tsx';
import RingilDataTable, { ColumnConfig, FilterType, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import { toBoolean } from '@app/pages/customer/supplier-notification/utils.ts';
import { AddCarrierUserSidebar } from '@app/pages/customer/carriers/carrier-detail/sidebar/AddCarrierUserSidebar.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';

interface ComponentProps {
  carrierData: CatalogCarrierDetailOutDTO;
  refreshData: () => void;
}

const CarrierDetailContacts: React.FC<ComponentProps> = ({ carrierData, refreshData }) => {
  const { tr } = useTranslationLgs();
  const { hasPermission } = useContext(AuthenticatedUserContext);

  const {
    cuChangeBlacklistCarrierUser,
  } = useContext(ApiContext);

  const [visibleAddCarrierUserForm, setVisibleAddCarrierUserForm] = useState(false);

  const editCarrierEnabled = hasPermission([PermissionTypesEnum.PERMISSION_MY_CARRIERS_RW]);
  const tableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);

  useEffect(() => {
    tableRef?.current.reloadData();
  }, [carrierData]);

  // RD-2492: blacklisted contacts are hidden by default; the "Show blacklisted" filter reveals them.
  const filtersConf = [
    {
      filterType: FilterType.Checkbox,
      placeholder: tr('CarrierDetailContacts.filterShowBlacklisted', 'Show blacklisted'),
      value: ['showBlacklisted'],
    },
  ];

  const loadDataApi = (filter: any, pageable: Pageable, onSuccess: (pagedData: PagedData<CarrierContactPersonOutDTO>) => void) => {
    // Non-blacklisted contacts first, then hide blacklisted ones unless the filter is enabled (RD-2492).
    const showBlacklisted = toBoolean(filter?.showBlacklisted);
    const contactPersons = [...(carrierData?.contactPersons || [])]
      .sort((a, b) => Number(a.isBlacklisted) - Number(b.isBlacklisted))
      .filter(p => (showBlacklisted ? true : !p.isBlacklisted));
    const page = pageable.pageNumber || 1;
    const size = pageable.pageSize || 10;
    const start = (page - 1) * size;
    const end = start + size;
    const items = contactPersons.slice(start, end);

    const data: PagedData<CarrierContactPersonOutDTO> = {
      data: items,
      total: contactPersons.length,
      page,
      size,
    };

    onSuccess(data);
  };

  const columnsConfig: ColumnConfig<CarrierContactPersonOutDTO>[] = [
    {
      id: 'name',
      header: 'CarrierDetailContacts.columnHeaderContactsName',
      headerDefault: 'Name',
      width: '25%',
      sortable: false,
      field: 'name',
      order: 1,
      render: (data: CarrierContactPersonOutDTO) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {data.name}
          </div>
        );
      },
    },
    {
      id: 'sendQuote',
      header: 'CarrierDetailContacts.columnHeaderContactsSendQuote',
      headerDefault: 'Send quotes',
      width: '10%',
      sortable: false,
      field: 'isBlacklisted',
      order: 2,
      render: (data: CarrierContactPersonOutDTO) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            <RingilInputSwitch
              label={''}
              value={!data.isBlacklisted}
              disabled={!editCarrierEnabled}
              onChange={() => cuChangeBlacklistCarrierUser(
                carrierData.carrierId,
                data.id,
                { toBlacklist: !data.isBlacklisted },
                () => {
                  refreshData();
                }
              )}
            />
          </div>
        );
      },
    },
    {
      id: 'carrierGroups',
      header: 'CarrierDetailContacts.columnHeaderContactsCarrierGroups',
      headerDefault: 'Carrier groups',
      width: '25%',
      sortable: false,
      field: 'carrierGroups',
      order: 3,
      render: (data: CarrierContactPersonOutDTO) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {data.carrierGroups?.map(carrierGroup => carrierGroup.groupName).join(',')}
          </div>
        );
      },
    },
    {
      id: 'contactInfo',
      header: 'CarrierDetailContacts.columnHeaderContactsCarrierContactInfo',
      headerDefault: 'Contact info',
      width: '40%',
      sortable: false,
      field: 'email',
      order: 4,
      render: (data: CarrierContactPersonOutDTO) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            <span>{data.phone}</span>
            <span className={'break-all'}>{data.email}</span>
          </div>
        );
      },
    },
  ];


  return (
    <>
      <div className={'flex flex-row'}>
        <COHeadline
          variant={'h2'}
          title={tr('CarrierDetailContacts.headlineContacts', 'Contacts')}
          icon={<SvgId className={'w-4 h-4 text-content-tertiary'} />}
        />
        {editCarrierEnabled && (
          <RingilButton
            icon={<SvgPlus />}
            label={tr('CarrierDetailContacts.buttonAddNewContact', 'Add contact')}
            mode={'text-only'}
            onClick={() => {
              setVisibleAddCarrierUserForm(true)
            }}
          />
        )}
      </div>
      <RingilDataTable
        ref={tableRef}
        columnConfig={columnsConfig as ColumnConfig<unknown>[]}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataApi}
        filterName={'carrier_detail_contacts_list'}
      />

      <AddCarrierUserSidebar
        visible={visibleAddCarrierUserForm}
        onHide={() => {
          setVisibleAddCarrierUserForm(false);
        }}
        onComplete={() => {
          refreshData();
          tableRef?.current.reloadData();
          setVisibleAddCarrierUserForm(false);
        }}
        carrierId={carrierData.carrierId}
      />
    </>
  );
};


export default CarrierDetailContacts;
